// LastSeenPresentation — twin of the Swift / Kotlin / C# `LastSeenPresentation` (2026-10-16).
//
// What a DM header says under the partner's name: presence or «Últ. vez …». The DEVICE is not named —
// the avatar's device badge already shows it (owner, 2026-10-16). Owner rulings (brief 2026-10-16, §B + §D + §E):
//   - the device-indicator plugin (shared/PLUGINS.md §8.1) is the SOURCE OF TRUTH for presence and
//     the last time, ALWAYS shown — no server switch (EnableLastActiveTime, show_last_active) hides it;
//   - the status's `last_activity_at` is only the fallback when the plugin has nothing;
//   - bots / integrations / self-DM get no phrase (the caller passes nothing for them).
//
// Pure: time comes in as `now`, calendar questions through ./time with an explicit zone, and the
// 12/24-hour choice is a parameter (the account preference the caller already honours).
import { civil, calendarDaysBetween } from './time';
import { t, tf, language } from '../i18n/i18n';
import type { DeviceIndicator } from './deviceIndicatorPresentation';

const SEPARATOR = ' · ';

// Fixed tables, not Intl: browsers' ICU data differs ("sep"/"sept", trailing dots) and the twins
// print a fixed text (same reason as relativeTime.ts).
const ES_WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const EN_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ES_MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** "13:05", or "1:05 p. m." / "1:05 PM" on a 12-hour account. */
function clock(hour: number, minute: number, twelveHour: boolean): string {
  if (!twelveHour) return pad2(hour) + ':' + pad2(minute);
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:${pad2(minute)} ${hour < 12 ? t('web.clock.am') : t('web.clock.pm')}`;
}

/**
 * «Últ. vez …» for one moment: just now / N min / today / yesterday / weekday (≤ 6 days) / date
 * (+ year when not this year). A moment in the future (clock skew) reads as "just now".
 */
function lastSeen(at: number, now: number, twelveHour: boolean, timeZone?: string): string {
  const delta = now - at;
  if (delta < 60_000) return t('web.lastSeen.justNow');
  if (delta < 3_600_000) return tf('web.lastSeen.minutes', Math.max(1, Math.floor(delta / 60_000)));
  const c = civil(at, timeZone);
  const time = clock(c.hour, c.minute, twelveHour);
  const days = calendarDaysBetween(at, now, timeZone);
  if (days <= 0) return tf('web.lastSeen.today', time);
  if (days === 1) return tf('web.lastSeen.yesterday', time);
  const es = language() === 'es';
  if (days <= 6) return tf('web.lastSeen.weekday', (es ? ES_WEEKDAYS : EN_WEEKDAYS)[c.weekday], time);
  const sameYear = civil(now, timeZone).year === c.year;
  const date = es
    ? `${c.day} ${ES_MONTHS[c.month - 1]}${sameYear ? '' : ' ' + c.year}`
    : `${EN_MONTHS[c.month - 1]} ${c.day}${sameYear ? '' : ', ' + c.year}`;
  return tf('web.lastSeen.date', date, time);
}

function liveWord(status: string): string | null {
  switch (status) {
    case 'online':
      return t('profile.status.online');
    case 'away':
      return t('profile.status.away');
    case 'dnd':
      return t('profile.status.dnd');
    default:
      return null;
  }
}

/**
 * The presence phrase, or null (the header then shows only the handle).
 *   live (online/away/dnd, or a device the plugin says is connected) → «En línea»
 *   offline → «Últ. vez hoy a las 13:05»: time = the plugin's latest device time; plugin silent →
 *   the status's `last_activity_at`; no time at all → null. No device word (the avatar badge says it).
 */
function subtitle(
  status: string | null | undefined,
  lastActivityAt: number,
  indicator: DeviceIndicator | null | undefined,
  now: number,
  twelveHour: boolean,
  timeZone?: string,
): string | null {
  const word = liveWord(status ?? '');
  const deviceLive = !!indicator && (indicator.desktopActive || indicator.mobileActive);
  if (word || deviceLive) {
    // A connected device with a lagging status still reads as online — the plugin is the truth.
    // No device word: the avatar's device badge already says it (owner, 2026-10-16).
    return word ?? t('profile.status.online');
  }
  if (status !== 'offline') return null; // unknown status (and ooo) says nothing rather than guess
  const pluginAt = indicator ? Math.max(indicator.desktopLastSeenAt, indicator.mobileLastSeenAt, indicator.lastSeenAt) : 0;
  if (indicator && pluginAt > 0) return lastSeen(pluginAt, now, twelveHour, timeZone);
  if (lastActivityAt > 0) return lastSeen(lastActivityAt, now, twelveHour, timeZone);
  return null;
}

/** The DM header's second line: `<phrase> · @username`, or `@username` alone. */
function headerLine(phrase: string | null, username: string): string {
  return phrase ? phrase + SEPARATOR + '@' + username : '@' + username;
}

/** One drill-in row's state: «Activo ahora», «Últ. vez …», or «Sin registro». */
function deviceRowText(isActive: boolean, lastSeenAt: number, now: number, twelveHour: boolean, timeZone?: string): string {
  if (isActive) return t('profile.device.active');
  if (lastSeenAt <= 0) return t('profile.device.none');
  return lastSeen(lastSeenAt, now, twelveHour, timeZone);
}

export const LastSeenPresentation = {
  subtitle,
  headerLine,
  lastSeen,
  deviceRowText,
} as const;
